/**
 * Peek Discover runs as its own app (apps-web/ai-discoverability) on its own domain.
 * Both URLs are overridable per environment and default to production.
 *
 * Points at `/login`, not `/`: Discover's `/` is its public landing page, which never
 * redirects a signed-in user to their dashboard. The auth token lives in sessionStorage
 * (ui-lib `accessToken.ts`), which is scoped per origin and per tab, so arriving from
 * dashboard.peek.us in a new tab is always an unauthenticated visit. `/login` sends
 * Discover admins on to /admin/dashboard and everyone else to /home once they sign in.
 */
export const DISCOVER_URL = process.env.PEEK_APP_DISCOVER_URL || "https://discover.peek.us/login";
/** Marketing page shown to customers who don't have Discover yet. */
export const DISCOVER_MARKETING_URL = process.env.PEEK_APP_DISCOVER_MARKETING_URL || "https://peek.us/discover";
/**
 * Whether the signed-in user has a Peek Discover account.
 *
 * The Discover role is the entitlement, and it arrives in one of two shapes depending on
 * the endpoint: a populated `Role` object or a bare alias string. This mirrors how Discover
 * itself reads it (`ai-discoverability/src/stores/AuthContext.tsx`).
 *
 * Deliberately does NOT consider `discoverOrganizations`: that is org scoping, not
 * entitlement, and the admin Users screen already treats a populated role as the test
 * (`EditUser.tsx` → `discoverAccess`). Keying on it too would let a user with stale
 * organizations but no role be sent to a Discover login they cannot get through.
 *
 * @param user
 * @returns true if the user can sign in to Discover, false otherwise
 */
export function hasDiscoverAccess(user) {
    const discoverRole = user === null || user === void 0 ? void 0 : user.discoverRole;
    if (typeof discoverRole === "string")
        return !!discoverRole;
    return !!((discoverRole === null || discoverRole === void 0 ? void 0 : discoverRole.alias) || (user === null || user === void 0 ? void 0 : user.discoverRoleAlias));
}
/**
 * Opens Discover in a new tab: the product for customers who have it,
 * the marketing page for everyone else.
 * @param hasAccess
 */
export function openDiscover(hasAccess) {
    window.open(hasAccess ? DISCOVER_URL : DISCOVER_MARKETING_URL, "_blank", "noopener,noreferrer");
}
